import { useState, useEffect } from "react";
import { Plus, Trash2, Copy, Tag, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface PromoCode {
  id: string;
  code: string;
  promo_type: string;
  value: string;
  description: string | null;
  max_uses: number;
  current_uses: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

const PROMO_TYPES = [
  { value: "plan_upgrade", label: "Plan Upgrade", desc: "Upgrades user to a specific plan" },
  { value: "extra_searches", label: "Extra Searches", desc: "Adds bonus daily searches" },
  { value: "discount_percent", label: "Discount %", desc: "Percentage discount on checkout" },
];

export function AdminPromoSection() {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [plans, setPlans] = useState<{ name: string; display_name: string }[]>([]);

  // New code form
  const [newCode, setNewCode] = useState("");
  const [newType, setNewType] = useState("plan_upgrade");
  const [newValue, setNewValue] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newMaxUses, setNewMaxUses] = useState(1);
  const [newExpires, setNewExpires] = useState("");

  const fetchCodes = async () => {
    setLoading(true);
    const { data } = await supabase.from("promo_codes").select("*").order("created_at", { ascending: false }) as { data: PromoCode[] | null };
    setCodes(data || []);
    setLoading(false);
  };

  const fetchPlans = async () => {
    const { data } = await supabase.from("plans").select("name, display_name");
    setPlans(data || []);
  };

  useEffect(() => { fetchCodes(); fetchPlans(); }, []);

  const generateCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let result = "JP-";
    for (let i = 0; i < 8; i++) result += chars[Math.floor(Math.random() * chars.length)];
    setNewCode(result);
  };

  const createCode = async () => {
    if (!newCode.trim()) { toast({ title: "Code is required", variant: "destructive" }); return; }
    if (!newValue.trim()) { toast({ title: "Value is required", variant: "destructive" }); return; }

    setCreating(true);
    try {
      const { error } = await supabase.from("promo_codes").insert({
        code: newCode.trim().toUpperCase(),
        promo_type: newType,
        value: newValue.trim(),
        description: newDesc.trim() || null,
        max_uses: newMaxUses,
        expires_at: newExpires || null,
      } as any);

      if (error) throw error;
      toast({ title: "Promo code created" });
      setNewCode(""); setNewValue(""); setNewDesc(""); setNewMaxUses(1); setNewExpires("");
      fetchCodes();
    } catch (err: any) {
      toast({ title: "Failed to create", description: err.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (id: string, active: boolean) => {
    await supabase.from("promo_codes").update({ is_active: !active } as any).eq("id", id);
    fetchCodes();
  };

  const deleteCode = async (id: string) => {
    if (!confirm("Delete this promo code?")) return;
    await supabase.from("promo_codes").delete().eq("id", id);
    fetchCodes();
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({ title: "Copied to clipboard" });
  };

  return (
    <div className="space-y-6">
      {/* Create new */}
      <div className="glass-card p-6 space-y-4">
        <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
          <Plus className="w-5 h-5 text-primary" /> Create Promo Code
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Code</label>
            <div className="flex gap-2">
              <input value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())}
                className="flex-1 bg-background border border-border/50 rounded-lg px-3 py-2 text-sm text-foreground font-mono"
                placeholder="JP-XXXXXXXX" />
              <button onClick={generateCode} className="px-3 py-2 bg-muted/50 rounded-lg text-xs text-muted-foreground hover:text-foreground">
                Generate
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Type</label>
            <select value={newType} onChange={e => { setNewType(e.target.value); setNewValue(""); }}
              className="w-full bg-background border border-border/50 rounded-lg px-3 py-2 text-sm text-foreground">
              {PROMO_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              {newType === "plan_upgrade" ? "Target Plan" : newType === "extra_searches" ? "Number of Searches" : "Discount %"}
            </label>
            {newType === "plan_upgrade" ? (
              <select value={newValue} onChange={e => setNewValue(e.target.value)}
                className="w-full bg-background border border-border/50 rounded-lg px-3 py-2 text-sm text-foreground">
                <option value="">Select plan...</option>
                {plans.filter(p => p.name !== "free").map(p => (
                  <option key={p.name} value={p.name}>{p.display_name}</option>
                ))}
              </select>
            ) : (
              <input type="number" value={newValue} onChange={e => setNewValue(e.target.value)}
                className="w-full bg-background border border-border/50 rounded-lg px-3 py-2 text-sm text-foreground"
                placeholder={newType === "extra_searches" ? "e.g. 50" : "e.g. 25"} min="1" />
            )}
          </div>

          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Max Uses</label>
            <input type="number" value={newMaxUses} onChange={e => setNewMaxUses(Number(e.target.value))}
              className="w-full bg-background border border-border/50 rounded-lg px-3 py-2 text-sm text-foreground" min="1" />
          </div>

          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Expires At (optional)</label>
            <input type="datetime-local" value={newExpires} onChange={e => setNewExpires(e.target.value)}
              className="w-full bg-background border border-border/50 rounded-lg px-3 py-2 text-sm text-foreground" />
          </div>

          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Description (optional)</label>
            <input value={newDesc} onChange={e => setNewDesc(e.target.value)}
              className="w-full bg-background border border-border/50 rounded-lg px-3 py-2 text-sm text-foreground"
              placeholder="Internal note..." />
          </div>
        </div>

        <button onClick={createCode} disabled={creating}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">
          {creating ? "Creating..." : "Create Promo Code"}
        </button>
      </div>

      {/* List */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" /> Promo Codes ({codes.length})
          </h3>
          <button onClick={fetchCodes} className="p-2 rounded-lg hover:bg-muted/30 text-muted-foreground">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : codes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No promo codes created yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/30 text-xs text-muted-foreground">
                  <th className="text-left py-2 px-2">Code</th>
                  <th className="text-left py-2 px-2">Type</th>
                  <th className="text-left py-2 px-2">Value</th>
                  <th className="text-left py-2 px-2">Uses</th>
                  <th className="text-left py-2 px-2">Expires</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-left py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(c => (
                  <tr key={c.id} className="border-b border-border/10 hover:bg-muted/10">
                    <td className="py-2 px-2 font-mono text-foreground font-medium">{c.code}</td>
                    <td className="py-2 px-2 text-muted-foreground">{PROMO_TYPES.find(t => t.value === c.promo_type)?.label || c.promo_type}</td>
                    <td className="py-2 px-2 text-muted-foreground">{c.value}</td>
                    <td className="py-2 px-2 text-muted-foreground">{c.current_uses}/{c.max_uses}</td>
                    <td className="py-2 px-2 text-muted-foreground text-xs">
                      {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "Never"}
                    </td>
                    <td className="py-2 px-2">
                      {c.is_active ? (
                        <span className="inline-flex items-center gap-1 text-primary text-xs"><CheckCircle2 className="w-3 h-3" /> Active</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-destructive text-xs"><XCircle className="w-3 h-3" /> Disabled</span>
                      )}
                    </td>
                    <td className="py-2 px-2 flex gap-1">
                      <button onClick={() => copyCode(c.code)} title="Copy code"
                        className="p-1.5 rounded hover:bg-muted/30 text-muted-foreground"><Copy className="w-3.5 h-3.5" /></button>
                      <button onClick={() => toggleActive(c.id, c.is_active)} title="Toggle active"
                        className="p-1.5 rounded hover:bg-muted/30 text-muted-foreground">
                        {c.is_active ? <XCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => deleteCode(c.id)} title="Delete"
                        className="p-1.5 rounded hover:bg-destructive/20 text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
